import angular, { IFormController, ILogService, IPromise, IQService, ITimeoutService } from 'angular';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { IcdOfficeService, TimeEntryService, TimeZoneDetectorService } from '../../../core/services';
import { UtilityService } from '../../../core/services/utility.service';
import templateUrl from './edit-time-entry-modal.component.html';


interface ProgramSelectItem {
    Value: number;
    Text: string;
    IsPinned: boolean;
}

interface StoreSelectItem {
    Value: number;
    Text: string;
    IsPinned: boolean;
}

interface ScheduledShow {
    ProgramID: number;
    StoreID: number;
}

interface OfficeSelectItem {
    IcdOfficeID: number;
}

interface TimeEntryVM {
    Id?: number;
    IcdOfficeID?: number;
    EmployeeID?: number;
    Date?: Date;
    Program?: ProgramSelectItem;
    ProgramId?: number;
    LocationId?: number;
    IcdOffice?: OfficeSelectItem;
    Store?: StoreSelectItem;
    WorkStartEndTime?: TimeInterval;
    BreakStartEndTime?: TimeInterval;
    AdditionalTimeEntries: TimeRecord[];
}

interface TimeInterval {
    Start?: Date;
    End?: Date;
    StartString?: string;
    EndString?: string;
}

interface TimeRecord {
    TimeEntryID?: number;
    StartEndTime: TimeInterval;
    startTime: kendo.ui.TimePicker;
    endTime: kendo.ui.TimePicker;
}

export interface Resolves {
    entryData: {
        timeEntryID?: number;
        employeeId: number;
        employeeName: string;
        startDate: Date;
        endDate: Date;
    };
    isEmployerView: boolean;
}

export const EditTimeEntryModalComponent = {
    templateUrl,
    bindings: {
        resolve: "<",
        dismiss: "&",
        close: "&"
    },
    controllerAs: "vm",
    controller: class EditTimeEntryModalController {
        resolve: Resolves;
        additionalTimeEntries: any;
        dismiss: ({ $value: any }) => void;
        close: ({ $value: any }) => void;
        workStartTime: kendo.ui.TimePicker;
        workEndTime: kendo.ui.TimePicker;
        breakStartTime: kendo.ui.TimePicker;
        breakEndTime: kendo.ui.TimePicker;
        editTimeEntryForm: IFormController;
        changeDateDebounced: () => void;

        ROAD_TRIP_PROGRAM_NAME = 'Road Trip';
        ROAD_TRIP_STORE_NAME = 'Road Trip';
        loading = true; // Used on every data load.
        // True while the time entry is being saved.
        submitting = false;
        // The id of the model being edited.
        timeEntryID: number;
        // The id of the employee for which time is being edited.
        employeeID: number;
        // The full name of the employee.
        employeeName: string;
        // The time entry being edited.
        timeEntry: TimeEntryVM;
        // The first and last dates selectable on date picker.
        startDate: Date;
        endDate: Date;
        // The list of all scheduled shows that the employee may have worked.
        scheduledShows: ScheduledShow[];
        // The list of all programs that can be selected for the given day.
        programs: ProgramSelectItem[] = [];
        // The list of all stores that can be selected for the given day.
        stores: StoreSelectItem[] = [];
        // The list of offices that can be selected.
        icdOffices = [];
        // The list of programs that appears on the schedule in some combination with
        // the current store selection.
        validPrograms: ProgramSelectItem[] = [];
        // The list of stores that appears on the schedule in some combination with the
        // current program selection.
        validStores: StoreSelectItem[] = [];
        // Indicate whether program and store selection is allowed.
        allowShowInput = false;
        // Indicate whether program and store selection are required.
        requireShowInput = false;

        isEmployerView: boolean;
        // Reference to the kendo DatePicker component.
        datePicker: kendo.ui.DatePicker;
        showAll = false;
        userUtcOffset = moment().utcOffset() / 60;

        constructor(private $q: IQService, private $timeout: ITimeoutService,
            private $log: ILogService, private divisionService,
            private icdOfficeService: IcdOfficeService, private rsScheduleService,
            private timeEntryService: TimeEntryService, private utilityService: UtilityService,
            private timeZoneDetectorService: TimeZoneDetectorService) {
            'ngInject'
        }

        $onInit() {
            this.timeEntryID = this.resolve.entryData.timeEntryID;
            this.timeEntry = { AdditionalTimeEntries: [] };
            this.employeeID = this.resolve.entryData.employeeId;
            this.employeeName = this.resolve.entryData.employeeName;
            this.startDate = this.resolve.entryData.startDate;
            this.endDate = this.resolve.entryData.endDate;
            // Indicate whether this is an employer view fo the time entry.
            this.isEmployerView = this.resolve.isEmployerView;

            // Load the time entry and all the data sources.
            const loading = this.loadTimeEntry(this.timeEntryID)
                .then(() => this.refreshIcdOffices()) // Trigger any cascading loads that would result from changing the date.
                .then(() => {
                    this.loading = false;
                });

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(loading, "Initial load failed"))
                .then(() => this.loading = false);
            this.changeDateDebounced = debounce(() => this.changeDate(), 500);
        }

        /**
         * Load the time entry with the given ID into the page,
         * or create it if the ID is invalid.
         * @param timeEntryID
            */
        loadTimeEntry(timeEntryID) {
            let getTimeEntry;
            if (timeEntryID) {
                getTimeEntry = this.timeEntryService.retrieveTimeEntry(timeEntryID);
            } else {
                // Pre-set to the first date on the selector.
                getTimeEntry = this.loadFirstAvailableDateAndOffices()
                    .then(firstDate => {
                        if (firstDate !== null) {
                            this.datePicker.min(firstDate)
                        } else {
                            alert("Employee was not active during this period.");
                        }

                        // Disable entry before this first date.
                        this.startDate = firstDate;
                        // Default to the first date for the new time entry.
                        return {
                            EmployeeID: this.employeeID,
                            Date: firstDate,
                            IsNew: true,
                            IsBreak: false,
                            AdditionalTimeEntries: []
                        }
                    });
            }
            return getTimeEntry.then(timeEntry => {
                // Create start/end time objects if none existed previously.
                if (!timeEntry.WorkStartEndTime) {
                    timeEntry.WorkStartEndTime = {
                        Start: null,
                        End: null
                    };
                }
                if (!timeEntry.BreakStartEndTime) {
                    timeEntry.BreakStartEndTime = {
                        Start: null,
                        End: null
                    };
                }
                normalizeTimeFormat(timeEntry);

                if (timeEntry.Date && timeEntry.WorkStartEndTime && timeEntry.WorkStartEndTime.Start &&
                    timeEntry.WorkStartEndTime.TimeZoneOffset) {
                    const workDate = this.convertDatetime(timeEntry.Date, timeEntry.WorkStartEndTime.Start);
                    timeEntry.WorkStartEndTime.Timezone = this.timeZoneDetectorService.guessTimezone(workDate,
                        timeEntry.WorkStartEndTime.TimeZoneOffset);
                }

                if (timeEntry.Date && timeEntry.BreakStartEndTime && timeEntry.BreakStartEndTime.Start &&
                    timeEntry.BreakStartEndTime.TimeZoneOffset) {
                    const breakDate = this.convertDatetime(timeEntry.Date, timeEntry.BreakStartEndTime.Start);
                    timeEntry.BreakStartEndTime.Timezone = this.timeZoneDetectorService.guessTimezone(breakDate,
                        timeEntry.BreakStartEndTime.TimeZoneOffset);
                }

                timeEntry.AdditionalTimeEntries.forEach(additionalTimeEntry => {
                    if (timeEntry.Date && additionalTimeEntry.StartEndTime &&
                        additionalTimeEntry.StartEndTime.Start &&
                        additionalTimeEntry.StartEndTime.TimeZoneOffset) {
                        const date = this.convertDatetime(timeEntry.Date, additionalTimeEntry.StartEndTime.Start);
                        additionalTimeEntry.StartEndTime.Timezone = this.timeZoneDetectorService.guessTimezone(date,
                            additionalTimeEntry.StartEndTime.TimeZoneOffset);
                    }
                });

                this.additionalTimeEntries = timeEntry.AdditionalTimeEntries;

                this.timeEntry = timeEntry;

                return this.timeEntry;
            })
                .then(() => this.onChangeIntervals()); // Update initial intervals based on the time entry.
        }

        convertDatetime(date, time) {
            const formatedTime = moment(time, "HH:mm").isValid() ? moment(time, "HH:mm").format('HH:mm:ss') : time;
            return moment(moment(date).format("MM/DD/YYYY") + ' ' +
                formatedTime, 'MM/DD/YYYY HH:mm:ss');
        }

        /**
         * Load the list of offices into the page for the given date.
         */
        loadIcdOffices(employeeID, date) {

            return this.icdOfficeService.retrieveIcdOffices({
                employeeID: employeeID,
                employmentDate: date
            })
                .then(icdOffices => {
                    icdOffices.forEach(office => {
                        office.OfficeName = kendo.format("{0} - {1}", office.IcdCorporationName, office.OfficeName);
                    });
                    this.icdOffices = icdOffices;
                    return this.icdOffices;
                });
        }

        /**
         * Load the list of programs into the page for the given date and office.
         */
        loadPrograms(employeeID, icdOfficeID, scheduleDate, selectedProgramID, showAll) {
            return this.rsScheduleService.retrieveEmployeePrograms(employeeID, icdOfficeID,
                scheduleDate, selectedProgramID, showAll)
                .then((programs: ProgramSelectItem[]) => {
                    return programs.sort(this.pinnedSort)
                })
                .then(programs => {
                    this.programs = programs;
                    this.timeEntry.Program = programs.find(p => p.Value === this.timeEntry.ProgramId);
                    return this.programs;
                });
        }

        /**
         * Load the list of stores into the page for the given date and office.
         */
        loadStores(employeeID, icdOfficeID, scheduleDate, selectedLocationID, showAll) {
            return this.rsScheduleService.retrieveEmployeeLocations(employeeID, icdOfficeID,
                scheduleDate, selectedLocationID, showAll)
                .then(stores => {
                    return stores.map(store => {
                        const name = store.StoreNumber
                            ? `${store.HostName}${store.StoreNumber}`
                            : store.HostName
                        return {
                            Value: store.Id,
                            Text: name,
                            IsPinned: store.IsPinned
                        };
                    });
                })
                .then(stores => {
                    return stores.sort(this.pinnedSort);
                })
                .then(stores => {
                    this.stores = stores;
                    this.timeEntry.Store = stores.find(p => p.Value === this.timeEntry.LocationId);
                    return this.stores;
                });
        }


        /**
         * Resolve with the first available pay date for the employee and load the offices worked
         * on that date into the controller.
         */
        loadFirstAvailableDateAndOffices() {
            let firstDate = this.$q.resolve<Date>(null);
            let currentDate = moment(this.startDate);
            const endDate = moment(this.endDate).endOf("day");
            while (currentDate.isBefore(endDate)) {
                const date = currentDate.toDate();
                // Chain the promise, and resolve the previously resolved date instead of loading
                // data if it was resolved earlier.
                firstDate = firstDate.then<Date>(entryDate => {
                    // If the entry date already resolved, don't resolve the next date.
                    return entryDate ?? this.loadIcdOfficesAndResolveDate(this.employeeID, date);
                });
                currentDate = currentDate.clone().add(1, 'day');
            }
            return firstDate;
        }

        /**
         * Load offices for the given employeeID, and resolve the date if the employee is
         * hired on a roster for that date.
         * @returns Promise<Date> A promise that resolves with the date, or null if none matches.
         */
        loadIcdOfficesAndResolveDate(employeeID, date): IPromise<Date> {
            return this.loadIcdOffices(employeeID, date)
                .then(icdOffices => {
                    return icdOffices.length ? date : null;
                });
        }

        /**
         * Load the list of employee schedules into the page for the given date and office.
         */
        loadEmployeeSchedules(employeeID, icdOfficeID, scheduleDate) {
            return this.rsScheduleService.retrieveEmployeeSchedules(employeeID, icdOfficeID, scheduleDate)
                .then(schedules => {
                    this.scheduledShows = schedules;
                });
        }

        /**
         * Refresh the list of ICD Offices based on the currently selected date.
         */
        refreshIcdOffices() {
            if (!this.timeEntry.Date) {
                // Also clear the selected office.
                this.timeEntry.IcdOfficeID = null;
                this.icdOffices = [];
                return this.refreshShows();
            }

            return this.loadIcdOffices(this.timeEntry.EmployeeID, this.timeEntry.Date)
                .then(icdOffices => {
                    // Attempt to keep the currently specified office as the selected office.
                    const office = icdOffices.filter(icdOffice => {
                        return icdOffice.Id === this.timeEntry.IcdOfficeID;
                    })[0] || (icdOffices.length === 1 ? icdOffices[0] : null); // Only preselect if there is only one office.
                    // If the office was not found in the list, either pick the first office by default
                    // or clear the selection (ie, the list was empty).
                    this.timeEntry.IcdOfficeID = office ? office.Id : null;
                    this.timeEntry.IcdOffice = office;
                })
                .then(() => this.refreshShows());
        }

        /**
         * Refresh data for shows based on the selected ICD Office and date.
         */
        refreshShows() {
            if (!this.timeEntry.IcdOfficeID) {
                this.timeEntry.Program = null;
                this.timeEntry.ProgramId = null;
                this.timeEntry.Store = null;
                this.timeEntry.LocationId = null;
                this.allowShowInput = false;
                this.requireShowInput = false;

                this.programs = [];
                this.stores = [];
                this.scheduledShows = [];

                return this.$q.when();
            }

            // Load the program list, store list, and list of shows for which the employee can be scheduled.
            return this.$q.all([
                this.loadPrograms(this.employeeID, this.timeEntry.IcdOfficeID, this.timeEntry.Date,
                    this.timeEntry.ProgramId, this.showAll),
                this.loadStores(this.employeeID, this.timeEntry.IcdOfficeID, this.timeEntry.Date,
                    this.timeEntry.LocationId, this.showAll),
                this.loadEmployeeSchedules(this.employeeID, this.timeEntry.IcdOfficeID, this.timeEntry.Date),
                this.divisionService.retrieveIcdOfficeDivisions(this.timeEntry.IcdOfficeID)
                    .then(divisions => this.updateAllowShowInput(divisions))
            ])
                .then(v => {
                    // Constrain data in the lookup lists based on whether they are possible assignments
                    // based on the schedule.
                    this.refreshValidPrograms();
                    this.refreshValidStores();
                });
        }

        validateProgramAndStores() {
            if (!this.editTimeEntryForm || !this.editTimeEntryForm.ProgramID) {
                // Only validate these fields if the ProgramID field is on the form.
                return;
            }
            if (this.timeEntry.LocationId && this.timeEntry.ProgramId) {
                //Get the selected program and store
                const selectedProgram = this.validPrograms.filter(program => {
                    return program.Value === this.timeEntry.ProgramId;
                })[0];
                const selectedStore = this.validStores.filter(store => {
                    return store.Value === this.timeEntry.LocationId;
                })[0];
                //If program is a road trip  check weather the store is the same
                //or not also check it the other way arroud
                if ((selectedProgram.Text === this.ROAD_TRIP_PROGRAM_NAME ||
                    selectedStore.Text === this.ROAD_TRIP_STORE_NAME)
                    && this.editTimeEntryForm && this.editTimeEntryForm.ProgramID) {
                    this.editTimeEntryForm.ProgramID.$setValidity("validProgramAndStores",
                        selectedProgram.Text === selectedStore.Text);
                    return;
                }
            }
            //This condition should always be valid if division doesnot support program and store entry
            this.editTimeEntryForm.ProgramID.$setValidity("validProgramAndStores", true);
        }

        updateAllowShowInput(divisions) {
            // Enable program and store input if any divisions use the RS schedule.
            this.allowShowInput = divisions.reduce((any, division) => {
                return any || division.UseRSSchedule;
            }, false);
            // Require program and store input for RS (divisionID === 29).
            this.requireShowInput = divisions.reduce((any, division) => {
                return any || division.Id === 29;
            }, false);
        }

        /**
         * Refresh the valid program list so that it only includes programs
         * that appear on an employee schedule in some combination with the
         * existing store selection.
         */
        refreshValidPrograms() {
            const selectedStore = this.stores.filter(store => {
                return store.Value === this.timeEntry.LocationId;
            })[0];
            this.timeEntry.Store = selectedStore;
            const validPrograms = this.programs.filter(program => {
                return program.Value === this.timeEntry.ProgramId
                    || program.IsPinned
                    || this.scheduledShows.some(schedule => {
                        return schedule.ProgramID === program.Value &&
                            (!selectedStore || schedule.StoreID === selectedStore.Value);
                    });
            });
            this.validPrograms = validPrograms;
        }

        /**
         * Refresh the valid store list so that it only includes stores
         * that appear on an employee schedule in some combination with the
         * existing program selection.
         */
        refreshValidStores() {
            const selectedProgram = this.programs.filter(program => {
                return program.Value === this.timeEntry.ProgramId
            })[0];
            this.timeEntry.Program = selectedProgram;
            const validStores = this.stores.filter(store => {
                return store.Value === this.timeEntry.LocationId
                    || store.IsPinned
                    || this.scheduledShows.some(schedule => {
                        return schedule.StoreID === store.Value &&
                            (!selectedProgram || schedule.ProgramID === selectedProgram.Value);
                    });
            });
            this.validStores = validStores;
        }

        getFormattedDate(isNew: boolean, date: Date, time: Date, timeZoneOffset: number) {
            //Create moment date by concatinating date and time
            let dateTime = this.convertDatetime(date, time);
            if (!isNew) {
                //Set the offset the was orignally defined without changing the time
                const offset = timeZoneOffset ? timeZoneOffset : 0
                dateTime = moment(dateTime).utcOffset(offset, true);
            } else {

                //Set the current offset
                dateTime = moment(dateTime).utcOffset(this.userUtcOffset, true);
            }
            //return the formatted date
            return dateTime.format("YYYY-MM-D HH:mm:ssZ");
        }

        createTimeEntryRequest(timeEntry) {
            const parent = {
                TimeEntryID: timeEntry.Id,
                Date: moment(timeEntry.Date).format("YYYY-MM-DD"),
                EmployeeID: timeEntry.EmployeeID,
                IcdOfficeID: timeEntry.IcdOfficeID,
                ProgramID: timeEntry.ProgramId,
                StoreID: timeEntry.LocationId,
                AdditionalTimeEntries: null
            };
            const isNew = timeEntry.Id === null || timeEntry.Id === undefined;
            const records = [];
            // Convert the individual times into records, starting with the regular work and meal times.
            if (timeEntry.WorkStartEndTime.Start && timeEntry.WorkStartEndTime.End) {
                records.push({
                    // Times need to be in 24h format to parse into TimeSpans correctly.
                    StartDateTime: this.getFormattedDate(isNew, timeEntry.Date,
                        timeEntry.WorkStartEndTime.Start,
                        timeEntry.WorkStartEndTime.TimeZoneOffset),
                    EndDateTime: timeEntry.WorkStartEndTime.End
                        ? this.getFormattedDate(isNew, timeEntry.Date,
                            timeEntry.WorkStartEndTime.End,
                            timeEntry.WorkStartEndTime.TimeZoneOffset) : null,
                    IsBreak: false,
                    IsLocalTime: timeEntry.IsLocalTime,
                    IsNew: timeEntry.IsNew,
                    IsDeleted: timeEntry.IsDeleted,
                    Comments: timeEntry.Comments,
                });
            }
            if (timeEntry.BreakStartEndTime.Start) {
                records.push({
                    // Times need to be in 24h format to parse into TimeSpans correctly.
                    StartDateTime: this.getFormattedDate(isNew, timeEntry.Date,
                        timeEntry.BreakStartEndTime.Start,
                        timeEntry.BreakStartEndTime.TimeZoneOffset),
                    EndDateTime: timeEntry.BreakStartEndTime.End
                        ? this.getFormattedDate(isNew, timeEntry.Date,
                            timeEntry.BreakStartEndTime.End,
                            timeEntry.BreakStartEndTime.TimeZoneOffset) : null,
                    IsBreak: true,
                    IsLocalTime: timeEntry.IsLocalTime,
                    // Always mark as new if there is a work entry. The work entry will become
                    // primary and the meal break will be added to the additional meal breaks.
                    IsNew: timeEntry.IsNew || records.length > 0,
                    IsDeleted: timeEntry.IsDeleted,
                    Comments: timeEntry.Comments
                });
            }
            for (const entry of timeEntry.AdditionalTimeEntries) {
                records.push({
                    TimeEntryID: entry.Id,
                    IsNew: entry.IsNew,
                    IsDeleted: entry.IsDeleted,
                    IsLocalTime: entry.StartEndTime ? entry.StartEndTime.IsLocalTime : false,
                    Date: moment(timeEntry.Date).format("YYYY-MM-DD"),
                    StartDateTime: entry.StartEndTime.Start ?
                        this.getFormattedDate(isNew, timeEntry.Date, entry.StartEndTime.Start,
                            entry.StartEndTime.TimeZoneOffset) : null,
                    EndDateTime: entry.StartEndTime.End ?
                        this.getFormattedDate(isNew, timeEntry.Date, entry.StartEndTime.End,
                            entry.StartEndTime.TimeZoneOffset) : null,
                    IsBreak: entry.IsBreak,
                    Comments: entry.Comments
                });
            }
            // Copy all time record fields from the main record to the parent time entry.
            const [main, ...additional] = records;
            Object.assign(parent, main);
            parent.AdditionalTimeEntries = additional;
            return parent;
        }

        onSubmit() {
            if (this.editTimeEntryForm.$invalid || !this.timeEntry.WorkStartEndTime.Start
                && !this.timeEntry.BreakStartEndTime.Start) {
                // Need at least a work or a break interval.
                return;
            }

            if (!this.timeEntry.BreakStartEndTime.Start && !confirm("You have not entered a meal break. Proceed?")) {
                return;
            }

            if (!confirm("I certify that the times that I entered today are correct and I have taken my meal and/or rest breaks for the day as required.")) {
                return;
            }

            if (this.isEmployerView && !confirm("Are you sure you want to change this employee's time card? Any changes that you make will be presented to the employee for acknowledgment.")) {
                return;
            }

            this.submitting = true;
            this.utilityService.showLoading();

            const request = this.createTimeEntryRequest(this.timeEntry);
            let saved;
            if (this.timeEntry.Id) {
                saved = this.timeEntryService.updateTimeEntryRecord(this.timeEntry.Id, request);
            } else {

                saved = this.timeEntryService.createTimeEntryRecord(request);
            }


            saved.then(() => {
                this.close({ $value: 'saved' });
            })
                .catch((ex) => {
                    this.$log.error(ex);
                    alert("Saving failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.submitting = false;
                    this.utilityService.hideLoading();
                });
        }

        onChangeDate() {
            this.changeDateDebounced();
        }

        onChangeIntervals() {
            // Wrap in timeout to wait until kendo objects have been instantiated and assigned.
            this.$timeout(() => {
                normalizeTimeFormat(this.timeEntry);
                // Update the min/max ranges for each time picker.
                if (this.workStartTime) {
                    // Skip if the kendo component hasn't initialized.
                    this.workStartTime.max(this.timeEntry.WorkStartEndTime.End);
                }
                if (this.workEndTime) {
                    this.workEndTime.min(this.timeEntry.WorkStartEndTime.Start);
                }
                if (this.breakStartTime) {
                    // Clear break start or end if they are no longer in valid intervals.
                    if (!isValidIntervalStart(this.timeEntry.BreakStartEndTime, this.timeEntry.WorkStartEndTime)) {
                        this.timeEntry.BreakStartEndTime.Start = null;
                        this.timeEntry.BreakStartEndTime.StartString = null;
                    }

                    this.breakStartTime.min(this.timeEntry.WorkStartEndTime.Start);
                    this.breakStartTime.max(this.timeEntry.BreakStartEndTime.End ||
                        this.timeEntry.WorkStartEndTime.End);
                }

                if (this.breakEndTime) {
                    if (!isValidIntervalEnd(this.timeEntry.BreakStartEndTime, this.timeEntry.WorkStartEndTime)) {
                        this.timeEntry.BreakStartEndTime.End = null;
                        this.timeEntry.BreakStartEndTime.EndString = null;
                    }

                    this.breakEndTime.min(this.timeEntry.BreakStartEndTime.Start ||
                        this.timeEntry.WorkStartEndTime.Start);
                    this.breakEndTime.max(this.timeEntry.WorkStartEndTime.End);
                }

                this.timeEntry.AdditionalTimeEntries.forEach(additional => {
                    // Clear start and end if they are no longer in valid intervals.
                    if (!isValidIntervalStart(additional.StartEndTime, this.timeEntry.WorkStartEndTime)) {
                        additional.StartEndTime.Start = null;
                        additional.StartEndTime.StartString = null;
                    }
                    if (!isValidIntervalEnd(additional.StartEndTime, this.timeEntry.WorkStartEndTime)) {
                        additional.StartEndTime.End = null;
                        additional.StartEndTime.EndString = null;
                    }

                    if (additional.startTime) {
                        additional.startTime.min(this.timeEntry.WorkStartEndTime.Start);
                        additional.startTime.max(additional.StartEndTime.End || this.timeEntry.WorkStartEndTime.End);
                    }

                    if (additional.endTime) {
                        additional.endTime.min(additional.StartEndTime.Start || this.timeEntry.WorkStartEndTime.Start);
                        additional.endTime.max(this.timeEntry.WorkStartEndTime.End);
                    }

                });
            }, 0);
        }

        onChangeIcdOffice() {
            const icdOffice = this.timeEntry.IcdOffice;
            this.timeEntry.IcdOfficeID = icdOffice ? icdOffice.IcdOfficeID : null;

            this.refreshShows()
                .catch(angular.noop);
        }

        onChangeProgram() {
            const program = this.timeEntry.Program;
            this.timeEntry.ProgramId = program ? program.Value : null;
            this.refreshValidStores();
            this.validateProgramAndStores();
        }

        onChangeStore() {
            const store = this.timeEntry.Store;
            this.timeEntry.LocationId = store ? store.Value : null;
            this.refreshValidPrograms();
            this.validateProgramAndStores();
        }

        addAdditionalMealBreak() {
            this.additionalTimeEntries.push({
                IsBreak: true,
                IsNew: true,
                StartEndTime: {
                    Start: null,
                    End: null
                }
            });
            this.onChangeIntervals();
        }

        deleteAdditionalTimeEntry(i) {
            const additional = this.additionalTimeEntries[i];
            if (additional.IsNew) {
                this.additionalTimeEntries.splice(i, 1);
            } else {
                additional.IsDeleted = 1; // This will be processed by the service.
            }
            additional.form.$setDirty(true);
        }

        cancel() {
            if (!this.editTimeEntryForm.$dirty || confirm("Discard changes?")) {
                this.dismiss({ $value: 'cancel' });
            }
        }

        /**
         * Sort items a and b in order such that items with IsPinned set to true
         * come before any other items, and are sorted alphabetically by their Text field otherwise.
         */
        pinnedSort(a, b) {
            return a.IsPinned !== b.IsPinned ? b.IsPinned - a.IsPinned : a.Text < b.Text ? -1 : 1;
        }

        changeDate() {
            this.loading = true;
            this.editTimeEntryForm.$setDirty();

            this.refreshIcdOffices()
                .catch(angular.noop)
                .then(() => {
                    // Trigger change handlers.
                    this.onChangeProgram();
                    this.onChangeStore();

                    this.loading = false;
                });
        }
    }
}

function normalizeTimeFormat(timeEntry: TimeEntryVM): void {
    // Ensure the time entry contains valid time intervals in the correct format.
    if (timeEntry.WorkStartEndTime) {
        timeEntry.WorkStartEndTime = normalizeTimeInterval(timeEntry.WorkStartEndTime);
    }
    if (timeEntry.BreakStartEndTime) {
        timeEntry.BreakStartEndTime = normalizeTimeInterval(timeEntry.BreakStartEndTime);
    }
    if (timeEntry.AdditionalTimeEntries) {
        for (const additional of timeEntry.AdditionalTimeEntries) {
            additional.StartEndTime = normalizeTimeInterval(additional.StartEndTime);
        }
    }
}

function normalizeTimeInterval(interval: TimeInterval) {
    if (interval.Start && !(interval.Start instanceof Date)) {
        interval.Start = moment(interval.Start, "HH:mm").toDate();
    }
    if (interval.End && !(interval.End instanceof Date)) {
        interval.End = moment(interval.End, "HH:mm").toDate();
    }
    return interval;
}

function isValidIntervalStart(interval: TimeInterval, container: TimeInterval) {
    if (!interval || !container || !interval.Start) {
        // A value is required for the start value.
        return false;
    }
    return isTimeBetween(interval.Start, container);
}

function isValidIntervalEnd(interval: TimeInterval, container: TimeInterval) {
    if (!interval || !container || !interval.End) {
        // A value is required for the start value.
        return false;
    }
    return isTimeBetween(interval.End, container);
}

function isTimeBetween(time: Date, container: TimeInterval) {
    let valid = true;
    if (container.Start) {
        valid = valid && moment(container.Start).isSameOrBefore(time);
    }
    if (container.End) {
        valid = valid && moment(container.End).isSameOrAfter(time);
    }
    return valid;
}
